import { useEffect, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '../Select';
import { Text } from '../Text';
import { clampYearToRange, type DatePickerValue, usePreviewRange, useRangeState } from './utils';

// Shared types and utilities for view components
type ViewProps = {
  value: DatePickerValue | null;
  onSelect: (date: Date) => void;
  rangeStart: Date | null;
  allowRange: boolean;
  minDate?: Date;
  /** Inclusive year span offered by the Year view (default: current year plus the 11 following). */
  yearRange?: { start: number; end: number };
};

// Day View Component
export function DayView({ value, onSelect, rangeStart, allowRange, minDate, yearRange }: ViewProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => clampYearToRange(new Date().getFullYear(), yearRange));
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Sync calendar month/year with the value's date, keyed on the date string so
  // a reference-only value change can't snap a browsed month back.
  const anchorDate = value ? ('date' in value ? value.date : value.startDate) : null;
  useEffect(() => {
    if (!anchorDate) return;
    const d = new Date(anchorDate);
    if (!Number.isNaN(d.getTime())) {
      setCurrentMonth(d.getMonth());
      setCurrentYear(d.getFullYear());
    }
  }, [anchorDate]);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const previewRange = usePreviewRange(
    rangeStart,
    hoveredDay,
    (day) => new Date(currentYear, currentMonth, day),
    (start) => start,
  );

  const { isSelected, isInRange } = useRangeState(value, rangeStart, previewRange, {
    toDate: (day: number) => new Date(currentYear, currentMonth, day),
    compare: (date1, date2) => date1.toDateString() === date2.toDateString(),
  });

  const handleDayClick = (day: number) => {
    onSelect(new Date(currentYear, currentMonth, day));
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = yearRange
    ? Array.from({ length: Math.max(yearRange.end - yearRange.start + 1, 1) }, (_, i) => yearRange.start + i)
    : Array.from({ length: 20 }, (_, i) => currentYear - 10 + i);

  return (
    <div className="flex flex-col gap-2 pt-1 px-2 max-h-[250px]">
      {/* Month/Year Navigation */}
      <div className="flex items-center gap-1">
        <IconButton
          icon="arrowLeft"
          iconSize={14}
          variant="secondary"
          size="icon"
          type="button"
          aria-label="Previous month"
          data-testid="date-picker-prev-month"
          className="size-6 flex items-center justify-center"
          onClick={() => {
            if (currentMonth === 0) {
              if (yearRange && currentYear - 1 < yearRange.start) return;
              setCurrentMonth(11);
              setCurrentYear(currentYear - 1);
            } else {
              setCurrentMonth(currentMonth - 1);
            }
          }}
        />
        <SelectRoot value={String(currentMonth)} onValueChange={(value) => setCurrentMonth(Number(value))}>
          <SelectTrigger className="w-auto min-w-[105px] text-xs h-6">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[250px]">
            {months.map((month, idx) => (
              <SelectItem key={month} value={String(idx)}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        <SelectRoot value={String(currentYear)} onValueChange={(value) => setCurrentYear(Number(value))}>
          <SelectTrigger className="w-auto min-w-[75px] text-xs h-6">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[250px]">
            {years.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        <IconButton
          icon="arrowRight"
          iconSize={14}
          variant="secondary"
          size="icon"
          type="button"
          aria-label="Next month"
          data-testid="date-picker-next-month"
          className="size-6 flex items-center justify-center"
          onClick={() => {
            if (currentMonth === 11) {
              if (yearRange && currentYear + 1 > yearRange.end) return;
              setCurrentMonth(0);
              setCurrentYear(currentYear + 1);
            } else {
              setCurrentMonth(currentMonth + 1);
            }
          }}
        />
      </div>

      {/* Calendar Grid - fixed height for 6 day rows (max possible in a month) to prevent layout shift */}
      <div className="grid grid-cols-7 grid-rows-[auto_repeat(6,minmax(0,1fr))] gap-x-[12px] gap-y-[7px] h-[214px]">
        {weekDays.map((day) => (
          <div key={day} className="text-xs text-grey-60 text-center py-1">
            {day}
          </div>
        ))}
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static spacer elements that never reorder
          <div key={`empty-${i}`} />
        ))}
        {days.map((day) => {
          const date = new Date(currentYear, currentMonth, day);
          const isBeforeMin = minDate && date < minDate;
          const selected = isSelected(day);
          const inRange = isInRange(day);
          const today = new Date();
          const isToday =
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
          return (
            <DayTile
              key={day}
              label={day}
              testId={`date-picker-day-${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`}
              selected={selected}
              allowRange={allowRange}
              inRange={inRange}
              isToday={isToday}
              disabled={isBeforeMin}
              onClick={() => handleDayClick(day)}
              onMouseEnter={() => allowRange && rangeStart && setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
            />
          );
        })}
      </div>
    </div>
  );
}

// Month View Component
export function MonthView({ value, onSelect, rangeStart, allowRange }: ViewProps) {
  const [visibleYear, setVisibleYear] = useState(new Date().getFullYear());
  const [hoveredMonth, setHoveredMonth] = useState<{ year: number; month: number } | null>(null);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 10 }, (_, i) => visibleYear - 5 + i);

  const previewRange = usePreviewRange(
    rangeStart,
    hoveredMonth,
    (item) => new Date(item.year, item.month, 1),
    (start) => new Date(start.getFullYear(), start.getMonth(), 1),
  );

  const { isSelected, isInRange } = useRangeState(value, rangeStart, previewRange, {
    toDate: (item: { year: number; month: number }) => new Date(item.year, item.month, 1),
    compare: (date1, date2) => date1.getTime() === date2.getTime(),
  });

  const handleMonthClick = (year: number, month: number) => {
    onSelect(new Date(year, month, 1));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-1 px-2 max-h-[250px] overflow-y-auto',
        '[&::-webkit-scrollbar]:w-2.5',
        '[&::-webkit-scrollbar]:h-2.5',
        '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
        '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white',
      )}
    >
      {years.map((year) => (
        <div key={year} className="flex flex-col gap-0.5">
          <Text className="text-xs text-grey-70">{year}</Text>
          <div className="grid grid-cols-3 gap-1">
            {months.map((month, monthIdx) => {
              const item = { year, month: monthIdx };
              const selected = isSelected(item);
              const inRange = isInRange(item);
              return (
                <DateTile
                  key={`${year}-${month}`}
                  label={month}
                  allowRange={allowRange}
                  inRange={inRange}
                  onClick={() => handleMonthClick(year, monthIdx)}
                  onMouseEnter={() => allowRange && rangeStart && setHoveredMonth(item)}
                  onMouseLeave={() => setHoveredMonth(null)}
                  selected={selected}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Quarter View Component
export function QuarterView({ value, onSelect, rangeStart, allowRange }: ViewProps) {
  const [visibleYear, setVisibleYear] = useState(new Date().getFullYear());
  const [hoveredQuarter, setHoveredQuarter] = useState<{ year: number; quarter: number } | null>(null);

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const years = Array.from({ length: 10 }, (_, i) => visibleYear - 5 + i);

  const previewRange = usePreviewRange(
    rangeStart,
    hoveredQuarter,
    (item) => new Date(item.year, item.quarter * 3 - 1, 1),
    (start) => {
      const rangeStartQuarter = Math.floor((start.getMonth() + 1) / 3);
      return new Date(start.getFullYear(), rangeStartQuarter * 3 - 1, 1);
    },
  );

  const { isSelected, isInRange } = useRangeState(value, rangeStart, previewRange, {
    toDate: (item: { year: number; quarter: number }) => new Date(item.year, item.quarter * 3 - 1, 1),
    compare: (date1, date2) => date1.getTime() === date2.getTime(),
  });

  const handleQuarterClick = (year: number, quarter: number) => {
    onSelect(new Date(year, quarter * 3 - 1, 1));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 py-1 px-2 max-h-[250px] overflow-y-auto',
        '[&::-webkit-scrollbar]:w-2.5',
        '[&::-webkit-scrollbar]:h-2.5',
        '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
        '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white',
      )}
    >
      {years.map((year) => (
        <div key={year} className="flex flex-col gap-0.5">
          <Text className="text-xs text-grey-70">{year}</Text>
          <div className="grid grid-cols-4 gap-1">
            {quarters.map((quarter, quarterIdx) => {
              const item = { year, quarter: quarterIdx + 1 };
              const selected = isSelected(item);
              const inRange = isInRange(item);
              return (
                <DateTile
                  key={`${year}-${quarter}`}
                  label={quarter}
                  onClick={() => handleQuarterClick(year, quarterIdx + 1)}
                  onMouseEnter={() => allowRange && rangeStart && setHoveredQuarter(item)}
                  onMouseLeave={() => setHoveredQuarter(null)}
                  allowRange={allowRange}
                  inRange={inRange}
                  selected={selected}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Year View Component
export function YearView({ value, onSelect, rangeStart, allowRange, yearRange }: ViewProps) {
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);

  const years = yearRange
    ? Array.from({ length: Math.max(yearRange.end - yearRange.start + 1, 1) }, (_, i) => yearRange.start + i)
    : Array.from({ length: 12 }, (_, i) => startYear + i);

  const previewRange = usePreviewRange(
    rangeStart,
    hoveredYear,
    (year) => new Date(year, 0, 1),
    (start) => new Date(start.getFullYear(), 0, 1),
  );

  const { isSelected, isInRange } = useRangeState(value, rangeStart, previewRange, {
    toDate: (year: number) => new Date(year, 0, 1),
    compare: (date1, date2) => date1.getFullYear() === date2.getFullYear(),
  });

  const handleYearClick = (year: number) => {
    onSelect(new Date(year, 0, 1));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-1 py-1 px-2 max-h-[250px] overflow-y-auto',
        '[&::-webkit-scrollbar]:w-2.5',
        '[&::-webkit-scrollbar]:h-2.5',
        '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
        '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white',
      )}
    >
      {years.map((year) => {
        const selected = isSelected(year);
        const inRange = isInRange(year);
        return (
          <DateTile
            key={year}
            label={year}
            onClick={() => handleYearClick(year)}
            onMouseEnter={() => allowRange && rangeStart && setHoveredYear(year)}
            onMouseLeave={() => setHoveredYear(null)}
            selected={selected}
            allowRange={allowRange}
            inRange={inRange}
          />
        );
      })}
    </div>
  );
}

type BaseTileProps = {
  label: string | number;
  selected: boolean;
  inRange: boolean;
  onClick: () => void;
  allowRange?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isToday?: boolean;
  disabled?: boolean;
  /** data-testid for the tile's button. */
  testId?: string;
};

export function DateTile({ label, selected, allowRange, inRange, onClick, onMouseEnter, onMouseLeave }: BaseTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-1 py-0 text-xs rounded-sm border border-grey-30 transition-colors cursor-pointer relative',
        'focus-ring',
        (selected || inRange) && 'bg-blue-50 text-white font-medium',
        !selected && !inRange && 'hover:bg-grey-10',
      )}
    >
      <Text className={cn('text-xs leading-[20px]', selected || inRange ? 'text-white font-medium' : 'text-grey-80')}>
        {label}
      </Text>
      {allowRange && <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className="absolute inset-[-3px]" />}
    </button>
  );
}

export function DayTile({
  label,
  selected,
  allowRange,
  inRange,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isToday,
  disabled,
  testId,
}: BaseTileProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'text-xs py-[1px] rounded-lg transition-colors relative cursor-pointer border',
        'focus-ring',
        (selected || inRange) && 'bg-blue-50 text-white font-medium rounded-lg border-blue-50',
        !selected && !inRange && !disabled && 'hover:bg-grey-10',
        isToday && !selected && !inRange ? 'border-grey-30' : 'border-transparent',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      <Text className={cn('text-xs leading-[20px]', selected || inRange ? 'text-white font-medium' : 'text-grey-80')}>
        {label}
      </Text>
      {allowRange && (
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className="absolute inset-y-[-4.5px] inset-x-[-7px]"
        />
      )}
    </button>
  );
}
